<template>
  <SideModal
    :show="show"
    @update:show="$emit('update:show', $event)"
    location="right"
    close-on-overlay-click
    width="640px"
    data-cy="sso-modal"
  >
    <div class="con" data-cy="sso-modal-content">
      <div class="d-flex align-center justify-space-between">
        <div class="title">Synergy ParentVUE SSO</div>
        <v-btn
          color="transparent"
          icon
          @click="$emit('update:show', false)"
          data-cy="sso-modal-close"
          ><v-icon>mdi-close</v-icon></v-btn
        >
      </div>

      <v-switch
        class="mt-5"
        color="secondary"
        v-model="ssoEnabled"
        label="Enable ParentVUE SSO"
        data-cy="synergy-sso-switch"
        hide-details
        :disabled="loading"
      ></v-switch>

      <v-text-field
        class="mt-5 custom-input"
        label="PXP URL"
        color="primary"
        v-model="pxpUrl"
        :disabled="loading"
        hide-details="auto"
        data-cy="synergy-pxp-url"
      ></v-text-field>
      <v-text-field
        class="mt-4 custom-input"
        label="Application Name"
        color="primary"
        v-model="applicationName"
        :disabled="loading"
        hide-details="auto"
        data-cy="synergy-application-name"
      ></v-text-field>

      <div v-if="ssoEnabled" class="mt-4" data-cy="synergy-sso-url-section">
        <p class="help-text">Paste this URL into ParentVUE:</p>
        <div class="sso-url-row">
          <span data-cy="synergy-parentvue-url">{{ parentVueSsoUrl }}</span>
          <v-btn
            icon
            color="transparent"
            size="small"
            data-cy="synergy-sso-copy-btn"
            @click="copyToClipboard(parentVueSsoUrl)"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </div>
      </div>

      <div class="d-flex justify-end mt-5">
        <v-btn :loading="loading" data-cy="synergy-sso-save-btn" @click="$emit('save')">
          Save
        </v-btn>
      </div>

      <SamlSettings v-if="show" />
    </div>
  </SideModal>
</template>

<script setup lang="ts">
import SideModal from "@/components/sideModal.vue";
import { useSchoolStore } from "@/stores/school";
import { copyToClipboard } from "@/utils";
import { synergyParentVueSsoUrl } from "./utils";
import SamlSettings from "./samlSettings.vue";
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    show: boolean;
    ssoEnabled: boolean;
    pxpUrl?: string;
    applicationName?: string;
    loading?: boolean;
  }>(),
  {
    pxpUrl: "",
    applicationName: "",
    loading: false,
  }
);

const emit = defineEmits<{
  (event: "update:show", val: boolean): void;
  (event: "update:ssoEnabled", val: boolean): void;
  (event: "update:pxpUrl", val: string): void;
  (event: "update:applicationName", val: string): void;
  (event: "save"): void;
}>();

const schoolStore = useSchoolStore();

const ssoEnabled = computed({
  get: () => props.ssoEnabled,
  set: (val: boolean) => emit("update:ssoEnabled", val),
});

const pxpUrl = computed({
  get: () => props.pxpUrl,
  set: (val: string) => emit("update:pxpUrl", val),
});

const applicationName = computed({
  get: () => props.applicationName,
  set: (val: string) => emit("update:applicationName", val),
});

const parentVueSsoUrl = computed(() => {
  const districtId = schoolStore.school?.district_id ?? "";
  const schoolId = schoolStore.school_id || schoolStore.school?.school_id || "";
  return synergyParentVueSsoUrl(districtId, schoolId);
});
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;

.con {
  padding: 20px 40px;
}

.title {
  color: $primary;
  font-size: 1.25rem;
  font-weight: 600;
}

.help-text {
  color: gray;
  margin: 0;
}

.sso-url-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-top: 8px;
  color: gray;
  word-break: break-all;
}
</style>
